/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import FormValidation from "../../atoms/formEnums/formValidation";
import "./formDatePartialPicker.css";

const FormDatePartialPicker = ({
    validation,
    errorText,
    value,
    onChange,
    id,
    disabled,
    minYear = new Date().getFullYear() - 4,
    maxYear = new Date().getFullYear() + 10,
    order = "MDY",
    show = "YMD",
}) => {
    const [day, setDay] = useState("");
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");
    useEffect(() => {
        if (!value) {
            setDay("");
            setMonth("");
            setYear("");
            return;
        }

        const parts = String(value).split("/");

        if (parts.length === 1) {
            setYear(parts[0]);
            setMonth("");
            setDay("");
        } else if (parts.length === 2) {
            setMonth(String(parseInt(parts[0], 10))); 
            setYear(parts[1]);
            setDay("");
        } else if (parts.length === 3) {
            const m = String(parseInt(parts[0], 10)); 
            const d = String(parseInt(parts[1], 10));
            const y = parts[2];

            setDay(d);
            setMonth(m);
            setYear(y);
        }
    }, [value]);

    const pad2 = (v) => v.toString().padStart(2, "0");

    const buildValue = (d, m, y) => {
        if (!y) return "";
        if (y && !m) return y;
        if (y && m && !d) return `${pad2(m)}/${y}`;
        if (y && m && d) return `${pad2(m)}/${pad2(d)}/${y}`;
        return "";
    };

    const handleSave = (d, m, y) => {
        const finalValue = buildValue(d, m, y);
        onChange({ target: { value: finalValue, id } });
    };

    const yearOptions = [
        { value: "", label: "Year" },
        ...Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
            const y = maxYear - i;
            return { value: y.toString(), label: y.toString() };
        }),
    ];

    const monthOptions = [
        { value: "", label: "Month" },
        ...Array.from({ length: 12 }, (_, i) => {
            const m = (i + 1).toString();
            return { value: m, label: pad2(m) };
        }),
    ];

    const getDaysInMonth = (y, m) => {
        if (!y || !m) return 31;
        return new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
    };

    const daysInMonth = getDaysInMonth(year, month);
    const dayOptions = [
        { value: "", label: "Day" },
        ...Array.from({ length: daysInMonth }, (_, i) => {
            const d = (i + 1).toString();
            return { value: d, label: pad2(d) };
        }),
    ];

    const handleYear = (opt) => {
        const y = opt?.value || "";
        setYear(y);
        if (!y) {
            setMonth("");
            setDay("");
            handleSave("", "", "");
            return;
        }
        handleSave(day, month, y);
    };

    const handleMonth = (opt) => {
        if (!year) return;
        const m = opt?.value || "";
        setMonth(m);
        if (!m) {
            setDay("");
            handleSave("", "", year);
            return;
        }
        handleSave(day, m, year);
    };

    const handleDay = (opt) => {
        if (!year || !month) return;
        const d = opt?.value || "";
        setDay(d);
        handleSave(d, month, year);
    };
    const normalized = (s) => (s || "").toUpperCase();
    const showSet = new Set(normalized(show).split(""));
    const orderArr = normalized(order).split("").filter((k) => showSet.has(k));

    const componentsByKey = {
        D: (
            <Select
                key="D"
                id={`${id}-day`}
                className="fdp-select"
                classNamePrefix="fdp"
                options={dayOptions}
                value={dayOptions.find((o) => o.value === day)}
                onChange={handleDay}
                isDisabled={!year || !month || disabled || !showSet.has("D")}
                menuPlacement="auto"
                menuPortalTarget={document.body}
            />
        ),
        M: (
            <Select
                key="M"
                id={`${id}-month`}
                className="fdp-select"
                classNamePrefix="fdp"
                options={monthOptions}
                value={monthOptions.find((o) => o.value === month)}
                onChange={handleMonth}
                isDisabled={!year || disabled || !showSet.has("M")}
                menuPlacement="auto"
                menuPortalTarget={document.body}
            />
        ),
        Y: (
            <Select
                key="Y"
                id={`${id}-year`}
                className="fdp-select"
                classNamePrefix="fdp"
                options={yearOptions}
                value={yearOptions.find((o) => o.value === year)}
                onChange={handleYear}
                isDisabled={disabled || !showSet.has("Y")}
                menuPlacement="auto"
                menuPortalTarget={document.body}
            />
        ),
    };
    return (
        <div className="fdp-container d-flex align-items-center justify-between">
            {orderArr.map((k) => componentsByKey[k]).filter(Boolean)}
        </div>
    );
};

FormDatePartialPicker.defaultProps = {
    validation: FormValidation.PENDING,
    disabled: false,
};

FormDatePartialPicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    id: PropTypes.string,
    disabled: PropTypes.bool,
};

export default FormDatePartialPicker;
